<template>
  <div :class="wrapperClasses">
    <div :class="tableContainerClasses" role="region" aria-label="Data table" tabindex="0">
      <div ref="tableRef" :class="tableStyleClasses">
        <slot/>
      </div>
    </div>
    <div v-if="caption" :class="captionClasses">{{ caption }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { cn } from '#bubba/shared/utils/cn';

/**
 * Blog Table Component
 *
 * A responsive table component for MDC (Markdown Components) that applies
 * styling to markdown tables with support for captions, striped rows,
 * and mobile responsiveness.
 *
 * Usage in markdown:
 * ::blog-table{caption="User Data Table" striped="true" bordered="true"}
 * | Name | Age | City |
 * | ---- | --- | ---- |
 * | John | 25  | NYC  |
 * | Jane | 30  | LA   |
 * ::
 */

interface Props {
  /**
   * Table caption
   */
  caption?: string;
  /**
   * Whether to add striped rows (accepts boolean or string "true"/"false" from MDC)
   */
  striped?: boolean | string;
  /**
   * Whether to add borders (accepts boolean or string "true"/"false" from MDC)
   */
  bordered?: boolean | string;
  /**
   * Whether to make table compact
   */
  compact?: boolean | string;
  /**
   * Table size variant
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether to show the thead row. Defaults to false because markdown always
   * emits a <thead> even when header cells are empty (key-value tables, etc.).
   * Pass header="true" only when the table genuinely has column headers.
   */
  header?: boolean | string;
  /**
   * Custom CSS classes
   */
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  striped: false,
  bordered: false,
  compact: false,
  size: 'medium',
  header: false,
});

// Helper to convert string "true"/"false" to boolean
const toBool = (val: boolean | string | undefined): boolean => {
  if (typeof val === 'string') return val === 'true';
  return !!val;
};

const isStriped = computed(() => toBool(props.striped));
const isBordered = computed(() => toBool(props.bordered));
const showHeader = computed(() => toBool(props.header));

/**
 * Wrapper styling for the entire table component
 */
const wrapperClasses = computed(() => {
  return cn('mb-6 overflow-hidden', props.class);
});

/**
 * Container for horizontal scrolling on mobile
 */
const tableContainerClasses = computed(() => {
  return cn('overflow-x-auto', {
    'border border-border rounded-lg': isBordered.value,
  });
});

/**
 * Table styling applied to the slot content
 */
const tableStyleClasses = computed(() => {
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  };

  return cn(
    // Table styles
    '[&_table]:w-full [&_table]:table-auto [&_table]:border-collapse',
    '[&_table]:bg-background',
    sizeClasses[props.size],

    // Header styles
    { '[&_thead]:hidden': !showHeader.value },
    '[&_thead]:bg-muted/50',
    '[&_th]:text-left [&_th]:font-medium [&_th]:text-foreground',
    '[&_th]:border-b [&_th]:border-border',

    // Body styles
    '[&_tbody]:divide-y [&_tbody]:divide-border',
    '[&_tr]:transition-colors [&_tr:hover]:bg-muted/30',
    '[&_td]:text-foreground/80 [&_td]:align-top',

    // Size-specific padding
    {
      '[&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2': props.size === 'small',
      '[&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3':
        props.size === 'medium',
      '[&_th]:px-5 [&_th]:py-3 [&_td]:px-5 [&_td]:py-3': props.size === 'large',
    },

    // Striped rows
    {
      '[&_tr:nth-child(even)]:bg-muted/30': isStriped.value,
    },

    // Bordered variant
    {
      '[&_th]:border-r [&_th]:border-border [&_th:last-child]:border-r-0':
        isBordered.value,
      '[&_td]:border-r [&_td]:border-border [&_td:last-child]:border-r-0':
        isBordered.value,
    },
  );
});

/**
 * Caption styling
 */
const captionClasses = 'mt-2 text-sm text-muted-foreground text-center';

// ── Copy-on-hover ──────────────────────────────────────────────────────────

const tableRef = ref<HTMLElement | null>(null);

const FADE_MS = 100;

const COPY_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>`;
const CHECK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

function setupCopyOnHover(): () => void {
  const container = tableRef.value;
  if (!container) return () => {};

  const cells = container.querySelectorAll<HTMLTableCellElement>('td');
  const cleanups: Array<() => void> = [];

  cells.forEach((td) => {
    td.style.position = 'relative';

    let btn: HTMLButtonElement | null = null;
    let fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;

    const showBtn = () => {
      // If a fade-out is in progress, cancel it and keep the existing button
      if (fadeOutTimer) {
        clearTimeout(fadeOutTimer);
        fadeOutTimer = null;
        if (btn) btn.style.opacity = '1';
        return;
      }
      if (btn) return;

      btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Copy');
      btn.title = 'Copy';
      Object.assign(btn.style, {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        background: 'none',
        border: 'none',
        padding: '0',
        cursor: 'pointer',
        color: 'var(--muted-foreground)',
        lineHeight: '0',
        zIndex: '1',
        opacity: '0',
        transition: `opacity ${FADE_MS}ms ease`,
      });
      btn.innerHTML = COPY_SVG;

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Collect text from td nodes, excluding the button itself
        const text = Array.from(td.childNodes)
          .filter((n) => n !== btn)
          .map((n) => n.textContent ?? '')
          .join('')
          .trim();

        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // Clipboard API unavailable — silent no-op
        }

        if (btn) {
          btn.innerHTML = CHECK_SVG;
          btn.style.color = 'oklch(0.72 0.19 149)';
        }

        if (resetTimer) clearTimeout(resetTimer);
        resetTimer = setTimeout(() => {
          if (btn) {
            btn.innerHTML = COPY_SVG;
            btn.style.color = 'var(--muted-foreground)';
          }
          resetTimer = null;
        }, 1500);
      });

      td.appendChild(btn);
      // Trigger fade-in on next frame so the transition fires
      requestAnimationFrame(() => {
        if (btn) btn.style.opacity = '1';
      });
    };

    const hideBtn = () => {
      if (!btn) return;
      const target = btn;
      target.style.opacity = '0';
      fadeOutTimer = setTimeout(() => {
        target.remove();
        if (btn === target) btn = null;
        fadeOutTimer = null;
        if (resetTimer) {
          clearTimeout(resetTimer);
          resetTimer = null;
        }
      }, FADE_MS);
    };

    td.addEventListener('mouseenter', showBtn);
    td.addEventListener('mouseleave', hideBtn);

    cleanups.push(() => {
      td.removeEventListener('mouseenter', showBtn);
      td.removeEventListener('mouseleave', hideBtn);
      if (fadeOutTimer) clearTimeout(fadeOutTimer);
      if (resetTimer) clearTimeout(resetTimer);
      btn?.remove();
      btn = null;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}

let destroyCopyHandlers: (() => void) | undefined;

onMounted(() => {
  // nextTick ensures slot content (MDC-rendered table) is in the DOM
  nextTick(() => {
    destroyCopyHandlers = setupCopyOnHover();
  });
});

onBeforeUnmount(() => {
  destroyCopyHandlers?.();
});
</script>
